/**
 * The device-history vocabulary: what each event kind is called, and which family it belongs to.
 *
 * The hand port of `DeviceEventKinds`. It is hand-written rather than generated for the reason
 * `rules.ts` is: these are two lookup tables with logic in them, not constants, and `ContractsGen`
 * emits only `const` members.
 *
 * **A kind with no entry reads as "Event", never as a blank badge**, which is the same call the C#
 * makes - an appended kind that reaches an older client should be unlabelled rather than invisible.
 * A test asserts every value of the generated enum has a label, so the day a kind is appended
 * server-side and forgotten here, the suite says so rather than a technician finding a row that
 * badges as "Event".
 */
import { DeviceEventCategory, DeviceEventKind } from './contracts.gen.js';

/** A short label for the kind - the row's badge. */
const LABELS: Partial<Record<DeviceEventKind, string>> = {
  [DeviceEventKind.Enrolled]: 'Enrolled',
  [DeviceEventKind.Approved]: 'Approved',
  [DeviceEventKind.Rejected]: 'Rejected',
  [DeviceEventKind.Archived]: 'Archived',
  [DeviceEventKind.Restored]: 'Restored',
  [DeviceEventKind.DecommissionRequested]: 'Decommissioned',
  [DeviceEventKind.AgentRemoved]: 'Agent removed',
  [DeviceEventKind.CameOnline]: 'Online',
  [DeviceEventKind.WentOffline]: 'Offline',
  [DeviceEventKind.Logon]: 'Sign-in',
  [DeviceEventKind.Logoff]: 'Sign-out',
  [DeviceEventKind.Locked]: 'Locked',
  [DeviceEventKind.Unlocked]: 'Unlocked',
  [DeviceEventKind.RemoteLogon]: 'Remote sign-in',
  [DeviceEventKind.Boot]: 'Started up',
  [DeviceEventKind.Shutdown]: 'Shut down',
  [DeviceEventKind.UnexpectedShutdown]: 'Unexpected shutdown',
  [DeviceEventKind.AgentStarted]: 'Agent started',
  [DeviceEventKind.SoftwareInstalled]: 'Installed',
  [DeviceEventKind.SoftwareUninstalled]: 'Uninstalled',
  [DeviceEventKind.SoftwareUpdated]: 'Updated',
  [DeviceEventKind.PatchInstalled]: 'Patch installed',
  [DeviceEventKind.PatchUninstalled]: 'Patch removed',
  [DeviceEventKind.UninstallRequested]: 'Uninstall requested',
  [DeviceEventKind.UpdateInstallRequested]: 'Updates requested',
  [DeviceEventKind.ActionCompleted]: 'Completed',
  [DeviceEventKind.ActionFailed]: 'Failed',
  [DeviceEventKind.RestartRequested]: 'Restart requested',
  [DeviceEventKind.MaintenanceStarted]: 'Maintenance on',
  [DeviceEventKind.MaintenanceEnded]: 'Maintenance off',
  [DeviceEventKind.RemoteSessionStarted]: 'Remote control',
  [DeviceEventKind.RemoteSessionEnded]: 'Remote ended',
  [DeviceEventKind.AgentVersionChanged]: 'Agent updated',
  [DeviceEventKind.InventoryRefreshRequested]: 'Inventory refresh',
  [DeviceEventKind.AgentUpdatePushed]: 'Update pushed',
  [DeviceEventKind.WakeRequested]: 'Wake sent',
  [DeviceEventKind.SoftwareInstallRequested]: 'Install requested',
  [DeviceEventKind.ShellCommandRun]: 'Command run',
  [DeviceEventKind.LocalUserCreated]: 'User created',
  [DeviceEventKind.LocalUserPasswordReset]: 'Password reset',
  [DeviceEventKind.LocalUserDisabled]: 'User disabled',
  [DeviceEventKind.LocalUserEnabled]: 'User enabled',
  [DeviceEventKind.ServiceControlled]: 'Service control',
  [DeviceEventKind.ServiceStartupChanged]: 'Startup type',
  [DeviceEventKind.LocalUserDeleted]: 'User deleted',
  [DeviceEventKind.ServiceStoppedUnexpectedly]: 'Service stopped',
  [DeviceEventKind.ServiceRecovered]: 'Service recovered',
  [DeviceEventKind.ServiceRecoveryChanged]: 'Recovery actions',
  [DeviceEventKind.ServiceAccountChanged]: 'Service account',
  [DeviceEventKind.LocalUserGroupsChanged]: 'User groups',
  [DeviceEventKind.MobileCommandRequested]: 'Phone command',
  [DeviceEventKind.MobileCommandCompleted]: 'Command result',
  [DeviceEventKind.LostModeStarted]: 'Lost mode on',
  [DeviceEventKind.LostModeStopped]: 'Lost mode off',
  [DeviceEventKind.LostModeLocation]: 'Location',
  [DeviceEventKind.MobilePolicyChanged]: 'Policy',
  [DeviceEventKind.MobileComplianceChanged]: 'Compliance',
  [DeviceEventKind.MobileManagementRemoved]: 'Management removed',
  [DeviceEventKind.WindowsPolicyChanged]: 'Windows policy',
  [DeviceEventKind.WindowsComplianceChanged]: 'Policy compliance',
  [DeviceEventKind.WindowsPolicyEnforced]: 'Policy enforced',
  [DeviceEventKind.BitLockerKeyEscrowed]: 'Recovery key',
  [DeviceEventKind.ScreenLocked]: 'Screen locked',
  [DeviceEventKind.DeviceLocked]: 'Locked',
  [DeviceEventKind.DeviceUnlocked]: 'Unlocked'
};

const SESSION = [
  DeviceEventKind.Logon,
  DeviceEventKind.Logoff,
  DeviceEventKind.Locked,
  DeviceEventKind.Unlocked,
  DeviceEventKind.RemoteLogon
];

const SOFTWARE = [
  DeviceEventKind.SoftwareInstalled,
  DeviceEventKind.SoftwareUninstalled,
  DeviceEventKind.SoftwareUpdated,
  DeviceEventKind.PatchInstalled,
  DeviceEventKind.PatchUninstalled,
  DeviceEventKind.ServiceStoppedUnexpectedly,
  DeviceEventKind.ServiceRecovered
];

const POWER = [
  DeviceEventKind.Boot,
  DeviceEventKind.Shutdown,
  DeviceEventKind.UnexpectedShutdown,
  DeviceEventKind.AgentStarted
];

const CONNECTIVITY = [DeviceEventKind.CameOnline, DeviceEventKind.WentOffline];

const MANAGEMENT = [
  DeviceEventKind.Enrolled,
  DeviceEventKind.Approved,
  DeviceEventKind.Rejected,
  DeviceEventKind.Archived,
  DeviceEventKind.Restored,
  DeviceEventKind.DecommissionRequested,
  DeviceEventKind.AgentRemoved,
  DeviceEventKind.UninstallRequested,
  DeviceEventKind.UpdateInstallRequested,
  DeviceEventKind.ActionCompleted,
  DeviceEventKind.ActionFailed,
  DeviceEventKind.RestartRequested,
  DeviceEventKind.MaintenanceStarted,
  DeviceEventKind.MaintenanceEnded,
  DeviceEventKind.RemoteSessionStarted,
  DeviceEventKind.RemoteSessionEnded,
  DeviceEventKind.AgentVersionChanged,
  DeviceEventKind.InventoryRefreshRequested,
  DeviceEventKind.AgentUpdatePushed,
  DeviceEventKind.WakeRequested,
  DeviceEventKind.SoftwareInstallRequested,
  DeviceEventKind.ShellCommandRun,
  DeviceEventKind.LocalUserCreated,
  DeviceEventKind.LocalUserPasswordReset,
  DeviceEventKind.LocalUserDisabled,
  DeviceEventKind.LocalUserEnabled,
  DeviceEventKind.ServiceControlled,
  DeviceEventKind.ServiceStartupChanged,
  DeviceEventKind.LocalUserDeleted,
  DeviceEventKind.ServiceRecoveryChanged,
  DeviceEventKind.ServiceAccountChanged,
  DeviceEventKind.LocalUserGroupsChanged,
  DeviceEventKind.MobileCommandRequested,
  DeviceEventKind.MobileCommandCompleted,
  DeviceEventKind.LostModeStarted,
  DeviceEventKind.LostModeStopped,
  DeviceEventKind.LostModeLocation,
  DeviceEventKind.MobilePolicyChanged,
  DeviceEventKind.MobileComplianceChanged,
  DeviceEventKind.MobileManagementRemoved,
  DeviceEventKind.WindowsPolicyChanged,
  DeviceEventKind.WindowsComplianceChanged,
  DeviceEventKind.WindowsPolicyEnforced,
  DeviceEventKind.BitLockerKeyEscrowed,
  DeviceEventKind.ScreenLocked,
  DeviceEventKind.DeviceLocked,
  DeviceEventKind.DeviceUnlocked
];

export function eventLabel(kind: DeviceEventKind): string {
  return LABELS[kind] ?? 'Event';
}

/** The family the history filter's combo narrows to. */
export function eventCategory(kind: DeviceEventKind): DeviceEventCategory {
  if (SESSION.includes(kind)) return DeviceEventCategory.Session;
  if (SOFTWARE.includes(kind)) return DeviceEventCategory.Software;
  if (POWER.includes(kind)) return DeviceEventCategory.Power;
  if (CONNECTIVITY.includes(kind)) return DeviceEventCategory.Connectivity;
  if (MANAGEMENT.includes(kind)) return DeviceEventCategory.Management;
  return DeviceEventCategory.Other;
}

/** The filter's options, in the order the XAML listed them. Null is every kind. */
export const HISTORY_CATEGORIES: readonly {
  readonly label: string;
  readonly category: DeviceEventCategory | null;
}[] = [
  { label: 'Everything', category: null },
  { label: 'Sign-ins & sessions', category: DeviceEventCategory.Session },
  { label: 'Software', category: DeviceEventCategory.Software },
  { label: 'Power', category: DeviceEventCategory.Power },
  { label: 'Management', category: DeviceEventCategory.Management },
  { label: 'Online / offline', category: DeviceEventCategory.Connectivity }
];
